import React from 'react';
import { BellIcon, BookOpenIcon, CalendarDaysIcon, ClipboardCheckIcon, FolderIcon, GraduationCapIcon, HouseIcon, LayoutDashboardIcon, MessageCircleIcon, MoreHorizontalIcon, SearchIcon, StoreIcon, UniversityIcon, WalletIcon } from 'lucide-react';
import { demoIdentity } from '../../data/landingVisual';

/**
 * The student home, rebuilt at preview scale.
 *
 * This is real Campuzon UI rather than a marketing mockup: the same navigation,
 * the same type scale, the same brand tokens, the same badge and card
 * treatments as the signed-in product. The content is demonstration data.
 */
const NAV = [{
  label: 'Dashboard',
  icon: LayoutDashboardIcon,
  active: true
}, {
  label: 'Academics',
  icon: GraduationCapIcon
}, {
  label: 'Housing',
  icon: HouseIcon
}, {
  label: 'Marketplace',
  icon: StoreIcon
}, {
  label: 'Campus',
  icon: UniversityIcon
}, {
  label: 'Money',
  icon: WalletIcon
}, {
  label: 'Messages',
  icon: MessageCircleIcon
}, {
  label: 'Calendar',
  icon: CalendarDaysIcon
}, {
  label: 'Files',
  icon: FolderIcon
}, {
  label: 'More',
  icon: MoreHorizontalIcon
}];
const SCHEDULE = [{
  time: '10:00',
  title: 'Database Systems',
  meta: 'CSC 321 · CIT Lab 1'
}, {
  time: '13:00',
  title: 'Engineering Maths II',
  meta: 'MEE 201 · Main Hall'
}, {
  time: '15:00',
  title: 'Project meeting',
  meta: 'Group discussion'
}];
const TASKS = [{
  title: 'Database assignment',
  meta: 'Due today',
  tone: 'danger' as const
}, {
  title: 'Operations research quiz',
  meta: 'Due tomorrow',
  tone: 'warn' as const
}, {
  title: 'Lab report 2',
  meta: 'Due Friday',
  tone: 'neutral' as const
}];
const TASK_TONE = {
  danger: 'text-danger-700',
  warn: 'text-warn-700',
  neutral: 'text-ink-subtle'
};
export function HeroDashboard() {
  return <div role="img" aria-label="The Campuzon student dashboard showing today's schedule, open tasks and housing budget" className="cz-preview overflow-hidden rounded-panel border border-line bg-surface shadow-[0_24px_60px_rgba(15,23,42,0.16)]" data-id="element-2184" data-mp="1uqjalj0o00cr">
      {/* window chrome */}
      <div className="flex items-center gap-1.5 border-b border-line bg-canvas px-3 py-2" data-id="element-2185" data-mp="24e5ess4qnlod">
        <span className="h-2 w-2 rounded-full bg-[#ef4444]" data-id="element-2186" data-mp="24cc7damawxfb" />
        <span className="h-2 w-2 rounded-full bg-[#f59e0b]" data-id="element-2187" data-mp="20b8oef8yil9v" />
        <span className="h-2 w-2 rounded-full bg-[#22c55e]" data-id="element-2188" data-mp="1ve068ent6h8m" />
      </div>

      {/* product header */}
      <div className="flex items-center gap-3 border-b border-line px-3 py-2" data-id="element-2189" data-mp="1qexdntp0m3hj">
        <span className="flex items-center gap-1.5" data-id="element-2190" data-mp="1lnnqczqxvc8t">
          <GraduationCapIcon aria-hidden className="h-4 w-4 text-brand-700" data-id="element-2191" data-mp="1lm47d1dwen3c" />
          <span className="text-[11px] font-bold tracking-tight text-ink" data-id="element-2192" data-mp="1gnsusiw7xyhf">CAMPUZON</span>
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-line bg-canvas px-2 py-1" data-id="element-2193" data-mp="1co0mm7aqlucl">
          <SearchIcon aria-hidden className="h-3 w-3 shrink-0 text-ink-subtle" data-id="element-2194" data-mp="17ncwroy8vjjy" />
          <span className="truncate text-[10px] text-ink-subtle" data-id="element-2195" data-mp="12pumnv8hqyad">Search Campuzon…</span>
        </span>
        <span className="relative" data-id="element-2196" data-mp="3eb3jzrlrryxk">
          <BellIcon aria-hidden className="h-4 w-4 text-ink-muted" data-id="element-2197" data-mp="3ee95yxz3qkp2" />
          <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-danger-700" data-id="element-2198" data-mp="3j9y7h6uombo0" />
        </span>
        <img src={demoIdentity.photo} alt="" className="h-5 w-5 shrink-0 rounded-full object-cover" data-id="element-2199" data-mp="3oaug1xpzalzr" />
      </div>

      <div className="flex" data-id="element-2200" data-mp="2vm6xi9wm6apw">
        {/* sidebar */}
        <nav className="hidden w-[104px] shrink-0 border-r border-line py-2 sm:block" data-id="element-2201" data-mp="2vni0i7i8jefy">
          <ul className="space-y-0.5 px-1.5" data-id="element-2202" data-mp="30l9q2uveqogz">
            {NAV.map(item => {
            const Icon = item.icon;
            return <li key={item.label} data-id="element-2203" data-mp="39ctlb70u6g77">
                  <span className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[10px] ${item.active ? 'bg-brand-50 font-semibold text-brand-800' : 'font-medium text-ink-muted'}`} data-id="element-2204" data-mp="39dv9md6v4z6v">
                    <Icon aria-hidden className={`h-3 w-3 shrink-0 ${item.active ? 'text-brand-700' : 'text-ink-subtle'}`} data-id="element-2205" data-mp="1xsfddp2ksh37" />
                    <span className="truncate" data-id="element-2206" data-mp="27jidanjrqq0w">{item.label}</span>
                  </span>
                </li>;
          })}
          </ul>
        </nav>

        {/* main */}
        <div className="min-w-0 flex-1 space-y-2.5 p-3" data-id="element-2207" data-mp="2ghc97xdmfhnj">
          <div data-id="element-2208" data-mp="2bjsvqzoqz8wn">
            <p className="text-[13px] font-bold text-ink" data-id="element-2209" data-mp="2bkc35mgruewf">Good afternoon, Amarachi!</p>
            <p className="mt-0.5 text-[10px] text-ink-muted" data-id="element-2210" data-mp="2qgjpasbqpqec">
              {demoIdentity.programme} · {demoIdentity.level}
            </p>
            <p className="text-[10px] text-ink-muted" data-id="element-2211" data-mp="2lj1jhat0rrak">
              University of Lagos · {demoIdentity.campus}
            </p>
            <div className="mt-1.5 flex gap-1.5" data-id="element-2212" data-mp="w4wkccz4w9k8">
              <span className="rounded-full bg-brand-50 px-1.5 py-0.5 text-[9px] font-semibold text-brand-800" data-id="element-2213" data-mp="r4qy5cbmvf9j">
                Verified student
              </span>
              <span className="rounded-full bg-canvas px-1.5 py-0.5 text-[9px] font-medium text-ink-muted" data-id="element-2214" data-mp="37q5n4ov6m2et">
                Enrolled
              </span>
            </div>
          </div>

          <ul className="grid grid-cols-3 gap-2" data-id="element-2215" data-mp="2szew3j1ez7cr">
            {[{
            value: '2',
            label: 'Classes today'
          }, {
            value: '4',
            label: 'Tasks due'
          }, {
            value: '₦450k',
            label: 'Housing budget'
          }].map(stat => <li key={stat.label} className="rounded-lg border border-line bg-canvas px-2 py-1.5" data-id="element-2216" data-mp="2o2y1q73mfwr8">
                <p className="cz-num text-[13px] font-bold text-ink" data-id="element-2217" data-mp="2a3y9yqktb1qu">{stat.value}</p>
                <p className="text-[9px] text-ink-muted" data-id="element-2218" data-mp="2f2jakr3z7nft">{stat.label}</p>
              </li>)}
          </ul>

          <div className="grid gap-2 sm:grid-cols-2" data-id="element-2219" data-mp="8bjudy5qq9dl">
            <div className="rounded-lg border border-line px-2 py-2" data-id="element-2220" data-mp="dan1rp970roq">
              <p className="text-[10px] font-semibold text-ink" data-id="element-2221" data-mp="d7r1vvdsbsxn">Today’s schedule</p>
              <ul className="mt-1.5 space-y-1.5" data-id="element-2222" data-mp="bqv4ngox8x9l">
                {SCHEDULE.map(row => <li key={row.time} className="flex gap-1.5" data-id="element-2223" data-mp="6sjvz9ad4ssy">
                    <span className="cz-num w-7 shrink-0 border-l border-brand-200 pl-1 text-[9px] font-semibold text-ink" data-id="element-2224" data-mp="1vtfwlyk5x7j">
                      {row.time}
                    </span>
                    <span className="min-w-0" data-id="element-2225" data-mp="ujyzh5dvlg5s">
                      <span className="block truncate text-[9px] font-semibold text-ink" data-id="element-2226" data-mp="uinwh7s98cfq">
                        {row.title}
                      </span>
                      <span className="block truncate text-[9px] text-ink-subtle" data-id="element-2227" data-mp="ksumucboa7e4">{row.meta}</span>
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="rounded-lg border border-line px-2 py-2" data-id="element-2228" data-mp="1da6z3did9oh8">
              <p className="text-[10px] font-semibold text-ink" data-id="element-2229" data-mp="18j75ah4jnmiy">My tasks</p>
              <ul className="mt-1.5 space-y-1.5" data-id="element-2230" data-mp="18h43z0xxy6yx">
                {TASKS.map(task => <li key={task.title} className="flex items-start gap-1.5" data-id="element-2231" data-mp="1gtnw0iycywvn">
                    <ClipboardCheckIcon aria-hidden className="mt-0.5 h-2.5 w-2.5 shrink-0 text-ink-subtle" data-id="element-2232" data-mp="1ku7tjnmug2ib" />
                    <span className="min-w-0" data-id="element-2233" data-mp="1us5k4hs6ebe3">
                      <span className="block truncate text-[9px] font-semibold text-ink" data-id="element-2234" data-mp="1usxexqdy853b">
                        {task.title}
                      </span>
                      <span className={`block text-[9px] ${TASK_TONE[task.tone]}`} data-id="element-2235" data-mp="23riqv2ox0lv4">
                        {task.meta}
                      </span>
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-lg bg-brand-50 px-2 py-1.5" data-id="element-2236" data-mp="2ih6uwbohdymj">
            <BookOpenIcon aria-hidden className="h-3 w-3 shrink-0 text-brand-700" data-id="element-2237" data-mp="2ifdlwpqeb2bd" />
            <p className="truncate text-[9px] font-medium text-brand-800" data-id="element-2238" data-mp="2ngjf0onywrlf">
              Register for second semester courses
            </p>
          </div>
        </div>
      </div>
    </div>;
}